import { mergeStyles } from "@fluentui/react";
import { useEffect, useReducer, useState } from "react";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import { customerFilterPillReducer, mapFilterPillMenuItemsPredicate } from "../../CustomerSearch/CustomerSearch.common";
import { ReducerType } from "../../CustomerSearch/CustomSearch.interfaces";
import { FILTER_PILL } from "./ConsumptionRevenueTab.constants";
import { dispatchLoadMenuItems, dispatchMenuItemsUpdate } from "./ConsumptionRevenueTab.helper";
import { ConsumptionRevenueTabFilterPillsProps } from "./ConsumptionRevenueTab.interfaces";

export const ConsumptionRevenueTabFilterPills = (props: ConsumptionRevenueTabFilterPillsProps) => {
  const [spaceLeft, setSpaceLeft] = useState(0);
  const [serviceFamilyState, serviceFamilyDispatch] = useReducer(customerFilterPillReducer, FILTER_PILL.INITIAL_STATE);
  const [serviceNameState, serviceNameDispatch] = useReducer(customerFilterPillReducer, FILTER_PILL.INITIAL_STATE);
  const [acrProductNamesState, acrProductNamesDispatch] = useReducer(
    customerFilterPillReducer,
    FILTER_PILL.INITIAL_STATE
  );

  // Events
  const dispatchLoadServiceFamilies = dispatchLoadMenuItems(serviceFamilyDispatch);
  const dispatchLoadServiceNames = dispatchLoadMenuItems(serviceNameDispatch);
  const dispatchLoadAcrProductNames = dispatchLoadMenuItems(acrProductNamesDispatch);
  const onServiceFamiliesUpdateHandler = dispatchMenuItemsUpdate(serviceFamilyDispatch);
  const onServiceNameUpdateHandler = dispatchMenuItemsUpdate(serviceNameDispatch);
  const onAcrProductNamesUpdateHandler = dispatchMenuItemsUpdate(acrProductNamesDispatch);

  const getAllMenuItems = () => {
    const input = props.data?.serviceFamilyGroups || [];
    // Get all service families
    const serviceFamilies = input.map((item) => item.serviceFamily).sort((a, b) => a.localeCompare(b));

    // Get all service names
    const serviceNames = input
      .flatMap((item) => item.services.map((service) => service.serviceName))
      .sort((a, b) => a.localeCompare(b));

    // Get all acrProductNames (flattened)
    const acrProductNames = input
      .flatMap((item) => item.services.flatMap((service) => service.acrProductNames))
      .sort((a, b) => a.localeCompare(b));
    return {
      serviceFamilies,
      serviceNames,
      acrProductNames
    };
  };
  const getFilteredMenuItems = () => {
    const selectedGroups = serviceFamilyState.selectedMenuItems;
    const selectedServices = serviceNameState.selectedMenuItems;

    // Filter groups and services, then flatMap to get all matching services
    const filteredServices =
      props?.data.serviceFamilyGroups
        ?.filter((group) => !selectedGroups.length || selectedGroups.includes(group.serviceFamily))
        .flatMap((group) => group.services) ?? [];

    const services = filteredServices.map((service) => service.serviceName);
    const acrProductNames = filteredServices
      .filter((service) => !selectedServices.length || selectedServices.includes(service.serviceName))
      .flatMap((service) => service.acrProductNames);

    return {
      services: Array.from(new Set(services)),
      acrProductNames: Array.from(new Set(acrProductNames))
    };
  };

  useEffect(() => {
    const { serviceFamilies, serviceNames, acrProductNames } = getAllMenuItems();

    dispatchLoadAcrProductNames(Array.from(acrProductNames).map(mapFilterPillMenuItemsPredicate));
    dispatchLoadServiceNames(Array.from(serviceNames).map(mapFilterPillMenuItemsPredicate));
    dispatchLoadServiceFamilies(Array.from(serviceFamilies).map(mapFilterPillMenuItemsPredicate));
    onServiceFamiliesUpdateHandler(["Security"]);
  }, []);

  useEffect(() => {
    const combinedServiceNames: string = serviceNameState.selectedMenuItems.join(";");
    const combinedServiceFamilies: string = serviceFamilyState.selectedMenuItems.join(";");
    const combinedAcrProductNames: string = acrProductNamesState.selectedMenuItems.join(";");
    const { services, acrProductNames } = getFilteredMenuItems();

    serviceNameDispatch({
      type: ReducerType.LoadMenuItems,
      data: services.map(mapFilterPillMenuItemsPredicate)
    });
    acrProductNamesDispatch({
      type: ReducerType.LoadMenuItems,
      data: acrProductNames.map(mapFilterPillMenuItemsPredicate)
    });

    props.onServiceNamesChange?.(combinedServiceNames);
    props.onServiceFamiliesChange?.(combinedServiceFamilies);
    props.onAcrProductNamesChange?.(combinedAcrProductNames);
  }, [
    serviceFamilyState.selectedMenuItems,
    serviceNameState.selectedMenuItems,
    acrProductNamesState.selectedMenuItems
  ]);

  return (
    <div
      className={mergeStyles({
        margin: "-4px",
        display: "flex",
        flexWrap: "wrap"
      })}
    >
      {FILTER_PILL.INITIAL_DATA.map((filterPillProps) => {
        const { key } = filterPillProps;
        const stateMapping: any = {
          serviceNames: { state: serviceNameState, handler: onServiceNameUpdateHandler },
          serviceFamilies: { state: serviceFamilyState, handler: onServiceFamiliesUpdateHandler },
          acrProductNames: { state: acrProductNamesState, handler: onAcrProductNamesUpdateHandler }
        };

        if (key !== undefined) {
          const mapping = stateMapping[key];
          filterPillProps.menuProps = {
            focusZoneProps: {
              shouldInputLoseFocusOnArrowKey: () => true
            },
            items: mapping.state.menuItems,
            onMenuOpened: (menu) => {
              let boundingRect: DOMRect | undefined;
              const target = menu?.target;
              if (target && typeof target !== "string") {
                if ("getBoundingClientRect" in target && typeof target.getBoundingClientRect === "function") {
                  boundingRect = target.getBoundingClientRect();
                } else if ("current" in target && target.current) {
                  boundingRect = target.current.getBoundingClientRect();
                }
              }
              if (boundingRect) {
                const lowerEdge = boundingRect.bottom; // Y coordinate of the bottom edge of the menu
                const windowHeight = window.innerHeight; // Height of the viewport
                const spaceLeft = windowHeight - lowerEdge; // Space available below the menu
                setSpaceLeft(spaceLeft);
              }
            },
            directionalHintFixed: true
          };
          filterPillProps.onApply = mapping.handler;
          filterPillProps.onCancel = mapping.handler;
          filterPillProps.onResetFilters = () => mapping.handler([]);
          filterPillProps.styles = {
            root: {
              margin: "4px",
              flex: "0 1 auto",
              minWidth: 0
            },
            subComponentStyles: {
              contextualMenu: {
                container: { height: "auto", transition: "min-width 1s ease-in-out" },
                list: {
                  maxHeight: `${Math.min(spaceLeft - 150, 400)}px`,
                  overflow: "auto",
                  "& .ms-ContextualMenu-link": { height: "auto" }
                }
              },
              callout: { calloutMain: {} }
            }
          };
        }

        return <CustomFilterPill {...filterPillProps} key={filterPillProps.key} />;
      })}
    </div>
  );
};
